import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import AdminSelectors from 'Stores/Admin/Selectors'
import AdminActions from 'Stores/Admin/Actions'
import { IFAvatar, IFTable, IFText, IFButton } from 'Components'

import { AdminProfileModal } from 'Containers'
import { Colors } from 'Theme'
import Styles from './AdminList.module.css'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import Skeleton from '@mui/material/Skeleton'
import { capitalizeFirstLetter } from '../../Utils/StringFunctions'
import ReportIcon from '@mui/icons-material/Report'
import RoleActions from 'Stores/Role/Actions'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

const AdminList = ({
  admins,
  adminFilters,
  fetchAdminList,
  paginationOffset,
  fetchAdminListRequestState,
  roles,
  updateAdminRoleRequestState,
  deleteAdminRequestState,
  loggedInAdmin,
  tablesHeight,
}) => {
  const { t } = useTranslation()
  const adminProfileModal = useRef()
  const [registeredCountries, setRegisteredCountries] = useState(false)
  const [columnVisibility, setColumnVisibility] = useState({
    country: false,
  })

  const [table, setTable] = useState()

  useEffect(() => {
    if (loggedInAdmin?.registeredCountries?.length > 1) {
      setRegisteredCountries(true)
    }
  }, [loggedInAdmin])

  useEffect(() => {
    setColumnVisibility({
      country: registeredCountries,
    })
  }, [registeredCountries])

  useEffect(() => {
    if (deleteAdminRequestState === RequestState.SUCCEEDED) {
      adminProfileModal.current.dismiss()
    }
  }, [deleteAdminRequestState])

  const loadMoreData = () => {
    if (
      fetchAdminListRequestState === RequestState.LOADING ||
      paginationOffset === null
    )
      return
    fetchAdminList(paginationOffset, failedRequest)
  }
  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchAdminListRequestState === RequestState.ERROR_0_NETWORK ||
      fetchAdminListRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchAdminListRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchAdminListRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchAdminListRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchAdminListRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    )
      setFailedRequest(true)
    if (fetchAdminListRequestState === RequestState.SUCCEEDED)
      setFailedRequest(false)
  }, [fetchAdminListRequestState])
  const handleRetry = () => {
    fetchAdminList(paginationOffset)
  }

  const adminTableColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        enableEditing: false,
        size: 160,
        header:
          fetchAdminListRequestState === RequestState.LOADING ||
          paginationOffset === 0 ? (
            <div className={Styles.SkeletonDiv}>
              <Skeleton
                variant="text"
                width="110px"
                animation="none"
                height={20}
                style={{ Left: '40px' }}
              />
            </div>
          ) : (
            <div className={Styles.HeaderAdminName}>{t('AdminList.Name')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < admins.length ? (
            <div className={Styles.AdminNameColumn}>
              <div className={Styles.AdminNameAvatar}>
                <IFAvatar
                  imageURL={row.original.avatarURL}
                  name={row.original.name}
                  size={40}
                />
              </div>
              <div className={Styles.AdminNameString}>
                <IFText>{capitalizeFirstLetter(row.original.name)}</IFText>
              </div>
            </div>
          ) : failedRequest ? (
            <div className={Styles.IconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'email',
        size: 240,
        enableEditing: false,
        header:
          fetchAdminListRequestState === RequestState.LOADING ||
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('AdminList.Email')}</IFText>
          ),
        Cell: ({ row }) => {
          return row.index < admins.length ? (
            <div>
              <div>
                <IFText>{capitalizeFirstLetter(row.original.email)}</IFText>
              </div>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },

      {
        accessorKey: 'country',
        size: 120,
        enableEditing: false,
        header:
          fetchAdminListRequestState === RequestState.LOADING ||
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('AdminList.Country')}</IFText>
          ),
        Cell: ({ row }) => {
          if (
            row.index < admins.length &&
            row.original.registeredCountries.length > 0
          ) {
            const countryCodes = row.original.registeredCountries
              .map((country) => country.name)
              .join(', ')
            return (
              <div>
                <IFText>{countryCodes}</IFText>
              </div>
            )
          } else if (failedRequest) {
            return <></>
          } else {
            return (
              <Skeleton
                variant="text"
                width="60%"
                animation="none"
                height={20}
              />
            )
          }
        },
      },
      {
        accessorKey: 'createdOn',
        enableEditing: false,
        header:
          fetchAdminListRequestState === RequestState.LOADING ||
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <div className={Styles.AdminColumnCreatedOn}>
              {t('AdminList.CreatedOn')}
            </div>
          ),

        Cell: ({ row }) => {
          return row.index < admins.length ? (
            <div className={Styles.AdminColumnCreatedOn}>
              <IFText>
                {moment(row.original.createdAt).format('MMM DD, YYYY LTS')}
              </IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'role',
        size: 120,
        header:
          fetchAdminListRequestState === RequestState.LOADING ||
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('AdminList.Role')}</IFText>
          ),
        muiTableBodyCellProps: {
          sx: {
            overflow: 'visible',
          },
        },

        Cell: ({ row }) => {
          return row.index < admins.length ? (
            <div>
              <IFText>{row.original.role.name}</IFText>
            </div>
          ) : failedRequest ? (
            <>
              <IFButton
                size="sm"
                color={Colors.red}
                text={t('IFTable.TryAgain')}
                onClick={handleRetry}
              />
            </>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
    ],
    [
      admins,
      fetchAdminListRequestState,
      roles,
      updateAdminRoleRequestState,
      failedRequest,
    ],
  )

  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  const navigate = useNavigate()
  const handleRowClick = (rowData) => {
    adminProfileModal.current.show(rowData, roles)
  }
  return (
    <div>
      {((fetchAdminListRequestState !== RequestState.SUCCEEDED &&
        !paginationOffset) ||
        admins) && (
        <>
          <IFTable
            columns={adminTableColumns}
            data={admins}
            fetchMoreOnBottomReached={fetchMoreOnBottomReached}
            paginationOffset={paginationOffset}
            filters={adminFilters}
            showSkeleton={
              fetchAdminListRequestState === RequestState.LOADING ||
              (fetchAdminListRequestState !== RequestState.SUCCEEDED &&
                paginationOffset === 0)
            }
            tableMaxHeight={tablesHeight}
            tableSetHeight={'100%'}
            hideColumnQuery={columnVisibility}
            tableRequestState={fetchAdminListRequestState}
            onRowClick={handleRowClick}
          />
        </>
      )}

      <AdminProfileModal ref={adminProfileModal} />
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchAdminList: (offset, shouldShowError) =>
      dispatch(AdminActions.fetchAdminList(offset, shouldShowError)),
  }
}
const mapStateToProps = (state) => ({
  fetchAdminListRequestState:
    AdminSelectors.getfetchAdminListRequestState(state),
  paginationOffset: AdminSelectors.getAdminListPaginationOffset(state),
  adminFilters: AdminSelectors.getAdminsFilters(state),
  updateAdminRoleRequestState:
    AdminSelectors.getupdateAdminRoleRequestState(state),
  deleteAdminRequestState: AdminSelectors.getDeleteAdminRequestState(state),
})
AdminList.propTypes = {
  fetchAdminList: PropTypes.func,
  fetchAdminListRequestState: PropTypes.number,
  paginationOffset: PropTypes.number,
  adminFilters: PropTypes.object,
  deleteAdminRequestState: PropTypes.number,
  updateAdminRoleRequestState: PropTypes.number,
  loggedInAdmin: PropTypes.object,
  tablesHeight: PropTypes.number,
  setUpdateAdminRoleRequestState: PropTypes.number,
  fetchAllRolesRequestState: PropTypes.number,
  roles: PropTypes.array,
  admins: PropTypes.array,
}
export default connect(mapStateToProps, mapDispatchToProps)(AdminList)
